import { createSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { initialState } from './reducer';
import { makeSelectShelfSelectionProfile } from 'containers/Ribbon/ShelvesRibbon/selectors';
import { makeSelectShelvesSelectedLastHistory } from 'containers/Configurator/selectors/history';
import { Color } from 'lib/modelBuilder/entities/Color';
import { Position, PositionAngle } from 'lib/modelBuilder/constants';
import { ShelfSelection } from 'lib/modelBuilder/entities/ShelfSelection';
import { makeSelectIsSameDrawersNumberInShelvesSelected } from 'containers/DrawersPanel/selectors';
import _ from 'lodash';

/**
 * Direct selector to the colorsPanel state domain
 */

const selectColorsPanelDomain = (state: ApplicationRootState) =>
  state.colorsPanel || initialState;

export type ColorPreviewItem = Color | undefined;

export const makeSelectCommonDoorColors = () =>
  createSelector(makeSelectShelvesSelectedLastHistory(), (shelves):
    | ColorPreviewItem[]
    | undefined => {
    const colors = new Map<PositionAngle, Color | undefined>();
    for (const shelf of shelves) {
      if (shelf.doors.length === 0) {
        continue;
        // return undefined;
      }
      for (const door of shelf.doors) {
        const position = door.position as PositionAngle;
        if (!colors.has(position)) {
          colors.set(position, door.color);
        } else if (colors.get(position)?.id !== door.color.id) {
          colors.delete(position);
          colors.set(position, undefined);
        }
      }
    }
    if (colors.size === 0) return undefined;
    const colorsArray: ColorPreviewItem[] = [];

    colorsArray.push(colors.get(PositionAngle.FrontLeft));
    colorsArray.push(colors.get(PositionAngle.FrontRight));

    if (
      colors.has(PositionAngle.BackLeft) ||
      colors.has(PositionAngle.BackRight)
    ) {
      colorsArray.push(colors.get(PositionAngle.BackLeft));
      colorsArray.push(colors.get(PositionAngle.BackRight));
    }

    return colorsArray;
  });

export const makeSelectCommonDrawersColors = () =>
  createSelector(
    makeSelectIsSameDrawersNumberInShelvesSelected(),
    makeSelectShelvesSelectedLastHistory(),
    (sameDrawers, shelves): ColorPreviewItem[] | undefined => {
      if (sameDrawers) {
        const colorsMap = new Map<number, Color | undefined>();
        for (const shelf of shelves) {
          for (const drawer of shelf.drawers) {
            const position = drawer.position as number;
            if (!colorsMap.has(position)) {
              colorsMap.set(position, drawer.color);
            } else if (colorsMap.get(position) !== drawer.color) {
              colorsMap.delete(position);
              colorsMap.set(position, undefined);
            }
          }
        }
        const colorsArray: ColorPreviewItem[] = [];

        for (const key of _.range(0, colorsMap.size)) {
          colorsArray.push(colorsMap.get(key));
        }

        return colorsArray.reverse();
      }
    },
  );

export const makeSelectCommonSelectionColor = () =>
  createSelector(
    makeSelectShelfSelectionProfile(),
    makeSelectShelvesSelectedLastHistory(),
    (shelvesSelectionProfile, shelvesSelected) => {
      return shelvesSelectionProfile.getCommonColor(shelvesSelected);
    },
  );

export const makeSelectSelectionHasDoor = (position: PositionAngle) =>
  createSelector(makeSelectShelvesSelectedLastHistory(), shelvesSelected =>
    ShelfSelection.checkDoorPresentAtPosition(position, shelvesSelected),
  );

export const makeSelectSelectionHasPane = (position: Position) =>
  createSelector(makeSelectShelvesSelectedLastHistory(), shelvesSelected =>
    ShelfSelection.checkPanePresentAtPosition(position, shelvesSelected),
  );

export { selectColorsPanelDomain };
